/** Shape + stage arithmetic for the campaign funnel.
 *
 *  Split from the component so the component file exports only
 *  components (react-refresh), and so the arithmetic that decides
 *  whether the parts make the whole is testable on its own.
 */
export type FunnelBreakdown = {
  total: number;
  pending: number;
  scheduled: number;
  made: number;
  rescheduled: number;
  no_show?: number;
  declined?: number;
  /** Every channel failed — nobody reached them (2026-09-29). Its own
   *  stage rather than part of `pending`: both are work still owed, but
   *  only this one says the platform failed rather than the
   *  beneficiary, and that is the difference an operator acts on. */
  contact_failed?: number;
};

export const COLORS = {
  made: "bg-emerald-500",
  scheduled: "bg-sky-500",
  pending: "bg-slate-500",
  // Orange, matching the list badge. Distinct from `declined`'s amber
  // and `no_show`'s red — a failed send is our problem, not an answer.
  contact_failed: "bg-orange-500",
  no_show: "bg-red-600",
  declined: "bg-amber-600",
} as const;

export type Stage = keyof typeof COLORS;

/** The stages, in funnel order, with anything the API didn't send
 *  treated as zero. Only non-empty stages are drawn — an always-present
 *  "Declined 0" is noise on the many campaigns that never see one. */
export function funnelStages(data: FunnelBreakdown) {
  const raw: Array<{ key: Stage; value: number }> = [
    { key: "made", value: data.made },
    { key: "scheduled", value: data.scheduled },
    { key: "pending", value: data.pending },
    { key: "contact_failed", value: data.contact_failed ?? 0 },
    { key: "no_show", value: data.no_show ?? 0 },
    { key: "declined", value: data.declined ?? 0 },
  ];
  return raw.filter((s) => s.value > 0);
}

/** What the bands do NOT account for.
 *
 *  Zero once every status has a band. It is surfaced rather than
 *  swallowed so a status added to the backend later shows up as an
 *  honest "other" instead of silently shrinking the chart again —
 *  which is exactly how this bug survived.
 */
export function unaccounted(data: FunnelBreakdown): number {
  const counted = funnelStages(data).reduce((s, x) => s + x.value, 0);
  return Math.max(0, data.total - counted);
}

